import { useState, useEffect, useRef, useCallback } from 'react';
import { X, PlayCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/App';

// ═══════════════════════════════════════════════════════════════════════════
// Onboarding video modal
// ───────────────────────────────────────────────────────────────────────────
// - Auto-shows on Dashboard mount if user.hide_onboarding_video !== true
// - Persists "do not show again" preference via POST /api/auth/onboarding-video
// - Dismissing without checkbox keeps the modal appearing on next login
// - Placeholder YouTube video; replace URL by editing PLACEHOLDER_VIDEO_URL
// - Mobile/tablet responsive, keyboard-dismissible, overlay dims background
// ═══════════════════════════════════════════════════════════════════════════

// Fallback YouTube URL if the backend config has never been set.
// The admin console populates `app_settings.onboarding_video.video_url` and
// the modal always prefers the admin-configured URL over this fallback.
export const PLACEHOLDER_VIDEO_URL = 'https://www.youtube.com/embed/IzgLr6Iyq1o';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function OnboardingVideoModal({ open, onClose, onPreferenceChange }) {
  const { token } = useAuth();
  const [hideFuture, setHideFuture] = useState(false);
  const [saving, setSaving] = useState(false);
  // ★ Admin-controlled config: { enabled, video_url }
  const [config, setConfig] = useState({ enabled: true, video_url: PLACEHOLDER_VIDEO_URL });
  const [configLoaded, setConfigLoaded] = useState(false);
  const closeBtnRef = useRef(null);
  const markedSeenRef = useRef(false);
  // ★ Prompt 3 — iframe ref lets us blank the src BEFORE React unmounts so
  //   YouTube's audio track stops immediately on close. Without this, closing
  //   the modal while the video is playing occasionally leaks a second or
  //   two of background audio (YouTube's autoplay keeps the media session
  //   alive during the React unmount tick).
  const iframeRef = useRef(null);

  // Pull admin-controlled config once per mount — cached for the life of this modal instance.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    axios.get(
      `${BACKEND_URL}/api/auth/onboarding-video-config`,
      { headers: { Authorization: `Bearer ${token}` } }
    ).then((res) => {
      if (cancelled) return;
      setConfig({
        enabled: res.data?.enabled !== false,
        video_url: res.data?.video_url || PLACEHOLDER_VIDEO_URL,
      });
      setConfigLoaded(true);
    }).catch(() => {
      if (!cancelled) setConfigLoaded(true); // fall back to defaults, never block UI
    });
    return () => { cancelled = true; };
  }, [token]);

  const handleClose = useCallback(async () => {
    if (saving) return;
    // ★ Stop playback synchronously before we tear anything else down.
    //   Blanking .src forces the YouTube iframe to release its media session
    //   so no audio leaks past the modal close.
    try {
      if (iframeRef.current) iframeRef.current.src = 'about:blank';
    } catch { /* iframe may already be detached — safe to ignore */ }
    if (hideFuture && token) {
      try {
        setSaving(true);
        await axios.post(
          `${BACKEND_URL}/api/auth/onboarding-video`,
          { hide_future: true },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        onPreferenceChange?.(true);
        toast.success("Got it — we won't show this again.");
      } catch {
        toast.error("Couldn't save your preference; we'll show it again next time.");
      } finally {
        setSaving(false);
      }
    }
    onClose?.();
  }, [saving, hideFuture, token, onPreferenceChange, onClose]);

  // Mark-seen ping — fire-and-forget on first open so seen_at is stored.
  useEffect(() => {
    if (!open || markedSeenRef.current || !token) return;
    markedSeenRef.current = true;
    axios.post(
      `${BACKEND_URL}/api/auth/onboarding-video`,
      { mark_seen: true },
      { headers: { Authorization: `Bearer ${token}` } }
    ).catch(() => { /* non-blocking */ });
  }, [open, token]);

  // Focus & ESC dismiss for accessibility + scrollbar lock.
  // ❗ Only lock body scroll when the modal will ACTUALLY render. Otherwise an
  // admin who has disabled the onboarding video (`config.enabled === false`)
  // would land on a Dashboard whose <body> is stuck at overflow:hidden —
  // scrollbar disappears until the next route change.
  useEffect(() => {
    if (!open || !configLoaded || !config.enabled) return;
    closeBtnRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, configLoaded, config.enabled, handleClose]);

  if (!open) return null;
  // ★ Respect admin "enabled" flag — don't render at all if the video has been
  //   globally disabled (and wait for config fetch to complete so we don't
  //   flash the default video before the admin's choice arrives).
  if (!configLoaded) return null;
  if (!config.enabled) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-3 sm:p-6 bg-slate-900/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="onb-title"
      data-testid="onboarding-video-modal"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-slate-200 bg-white">
          <div className="flex items-center gap-2">
            <PlayCircle className="w-5 h-5 text-[#0891b2]" />
            <h2 id="onb-title" className="text-base sm:text-lg font-semibold text-slate-800" data-testid="onboarding-video-title">
              Welcome — quick tour (2 min)
            </h2>
          </div>
          <button
            ref={closeBtnRef}
            onClick={handleClose}
            className="w-8 h-8 inline-flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0891b2]"
            aria-label="Close onboarding video"
            data-testid="onboarding-close-x"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-auto">
          <div className="relative w-full bg-black" style={{ aspectRatio: '16 / 9' }}>
            <iframe
              ref={iframeRef}
              src={config.video_url || PLACEHOLDER_VIDEO_URL}
              title="Precision Fish Detection — Onboarding"
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
              data-testid="onboarding-video-frame"
            />
          </div>
          <div className="px-4 sm:px-6 py-4 text-sm text-slate-600 space-y-2">
            <p>
              This short clip walks through the main sections of Precision Fish Detection — uploading a
              cycle, reading the missing-fish intelligence panel, and comparing profiles.
            </p>
            <p className="text-xs text-slate-500 italic">
              You can re-open this video any time from <span className="font-medium">Dashboard → Help</span>.
            </p>
          </div>
        </div>

        <div className="px-4 sm:px-6 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between flex-wrap gap-3">
          <label className="inline-flex items-center gap-2 text-xs sm:text-sm text-slate-700 cursor-pointer select-none" data-testid="onboarding-hide-label">
            <Checkbox
              checked={hideFuture}
              onCheckedChange={(v) => setHideFuture(v === true)}
              data-testid="onboarding-hide-checkbox"
            />
            Do not show me this again
          </label>
          <div className="flex items-center gap-2 ml-auto">
            <Button
              size="sm"
              variant="outline"
              onClick={handleClose}
              disabled={saving}
              data-testid="onboarding-close-btn"
              className="min-w-[84px]"
            >
              {hideFuture ? (saving ? 'Saving…' : 'Close') : 'Close'}
            </Button>
            <Button
              size="sm"
              onClick={handleClose}
              disabled={saving}
              className="bg-[#0891b2] hover:bg-[#0e7490] text-white min-w-[84px]"
              data-testid="onboarding-continue-btn"
            >
              Continue
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
